import { AlertCircle, MessageSquare } from "lucide-react";

import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import { LogDetailPanel } from "./LogDetailPanel";
import { LogsTable } from "./LogsTable";
import { LogsToolbar } from "./LogsToolbar";
import { MAX_RESULTS, useConversationLogs } from "./use-conversation-logs";

export function ConversationLogsPage(): React.ReactElement {
  const {
    entries,
    total,
    isLoading,
    error,
    filters,
    setFilters,
    search,
    selectedEntry,
    selectEntry,
  } = useConversationLogs();

  return (
    <div data-testid="conversation-logs-page" className="space-y-6">
      <LogsToolbar
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={search}
        isLoading={isLoading}
      />

      {error && (
        <div
          data-testid="logs-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={search}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && (
        <div data-testid="logs-loading" className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && entries.length === 0 && !error && (
        <Card data-testid="empty-logs-card" className="mx-auto max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>No Logs Found</CardTitle>
            <CardDescription>
              No conversation logs match the current filters. Try adjusting the time range or
              filters.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && entries.length > 0 && (
        <div className="flex gap-6">
          <div className="min-w-0 flex-1">
            <div className="mb-2 text-sm text-muted-foreground">
              Showing {total > MAX_RESULTS ? `first ${entries.length}` : entries.length} of {total}{" "}
              results
            </div>
            <Card>
              <CardContent className="p-0">
                <LogsTable entries={entries} selectedEntry={selectedEntry} onSelect={selectEntry} />
              </CardContent>
            </Card>
          </div>

          {selectedEntry && (
            <div className="w-96 shrink-0">
              <LogDetailPanel entry={selectedEntry} onClose={() => selectEntry(null)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
